import { useState, useMemo } from 'react';
import { formatDate, getFileTestDate } from '../../util/GenericUtil';
import { calculateAllSystemScores, getScoreColor } from '../../util/SystemScoringUtil';
import { convertDateToTimestamp, applyMinimumGapConstraint } from '../../util/TimeBasedScaling';
import bloodGroupings from '../../../lambda/health-recommendations/blood_groupings.json';

function OrganSystemHeatmap({ bloodTestFiles, getFxRange, sex = 'male', selectedSystem, onSystemClick }) {
    const [hoveredSystem, setHoveredSystem] = useState(null);
    const [hoveredPoint, setHoveredPoint] = useState(null); // { systemId, pointIdx, x, y }
    // Process data: group by date and calculate system scores
    const { chartData, systemIds } = useMemo(() => {
        if (!bloodTestFiles || bloodTestFiles.length === 0) return { chartData: [], systemIds: [] };

        // Group tests by date
        const fileData = {};
        bloodTestFiles.forEach(file => {
            if (!file.data || !file.data.body) return;

            const testDate = getFileTestDate(file);
            if (!testDate) return;

            if (!fileData[testDate]) {
                fileData[testDate] = [];
            }
            fileData[testDate].push(...file.data.body);
        });

        const sortedTestDates = Object.keys(fileData).sort();

        if (sortedTestDates.length === 0) return { chartData: [], systemIds: [] };

        // Wrap getFxRange to include sex parameter
        const getFxRangeWithSex = (markerName) => getFxRange(markerName, sex);

        // Calculate system scores for each date
        const dataPoints = sortedTestDates.map(testDate => {
            const testsForDate = fileData[testDate];
            const systemScores = calculateAllSystemScores(
                testsForDate,
                bloodGroupings,
                getFxRangeWithSex
            );

            const point = {
                testdate: testDate,
                timestamp: convertDateToTimestamp(testDate),
                scores: {}
            };

            // Store scores for each system
            Object.entries(systemScores).forEach(([systemId, data]) => {
                point.scores[systemId] = data.score;
            });

            return point;
        });

        // Apply time-based spacing
        const adjustedData = applyMinimumGapConstraint(dataPoints);

        // Get system IDs in consistent order
        const systemIds = Object.keys(bloodGroupings);

        return { chartData: adjustedData, systemIds };
    }, [bloodTestFiles, getFxRange, sex]);

    if (!chartData || chartData.length === 0) {
        return (
            <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#666',
                fontStyle: 'italic'
            }}>
                Upload blood tests to see organ system health trends
            </div>
        );
    }

    if (chartData.length === 1) {
        return (
            <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#666',
                fontStyle: 'italic'
            }}>
                Multiple test dates required for trend visualization
            </div>
        );
    }

    // Calculate dimensions
    const width = 900;
    const height = 400;
    const padding = { top: 20, right: 120, bottom: 40, left: 150 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    const rowHeight = chartHeight / systemIds.length;

    // Get time domain
    const minTime = chartData[0].adjustedTimestamp;
    const maxTime = chartData[chartData.length - 1].adjustedTimestamp;
    const timeRange = maxTime - minTime;

    // X-scale function
    const xScale = (timestamp) => {
        return ((timestamp - minTime) / timeRange) * chartWidth;
    };

    // Format timestamp for display
    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const yyyymmdd = Number(
            date.getFullYear() +
            String(date.getMonth() + 1).padStart(2, '0') +
            String(date.getDate()).padStart(2, '0')
        );
        return formatDate(yyyymmdd);
    };

    return (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
            <svg width={width} height={height} style={{ maxWidth: '100%', height: 'auto' }}>
                <defs>
                    {/* Create gradients for each system between each pair of timestamps */}
                    {systemIds.map((systemId, sysIdx) => {
                        return chartData.slice(0, -1).map((point, idx) => {
                            const nextPoint = chartData[idx + 1];
                            const score1 = point.scores[systemId];
                            const score2 = nextPoint.scores[systemId];
                            const color1 = getScoreColor(score1);
                            const color2 = getScoreColor(score2);

                            return (
                                <linearGradient
                                    key={`gradient-${systemId}-${idx}`}
                                    id={`gradient-${systemId}-${idx}`}
                                    x1="0%"
                                    y1="0%"
                                    x2="100%"
                                    y2="0%"
                                >
                                    <stop offset="0%" stopColor={color1} />
                                    <stop offset="100%" stopColor={color2} />
                                </linearGradient>
                            );
                        });
                    })}
                </defs>

                {/* Y-axis labels (system names) */}
                {systemIds.map((systemId, idx) => {
                    const y = padding.top + (idx * rowHeight) + (rowHeight / 2);
                    const isSelected = selectedSystem === systemId;
                    const isHovered = hoveredSystem === systemId;

                    return (
                        <g key={`label-${systemId}`}>
                            <text
                                x={padding.left - 10}
                                y={y}
                                textAnchor="end"
                                dominantBaseline="middle"
                                style={{
                                    fontSize: '14px',
                                    fontWeight: (isSelected || isHovered) ? 'bold' : 'normal',
                                    fill: isSelected ? '#000' : isHovered ? '#333' : '#666',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease'
                                }}
                                onClick={() => onSystemClick && onSystemClick(systemId)}
                                onMouseEnter={() => setHoveredSystem(systemId)}
                                onMouseLeave={() => setHoveredSystem(null)}
                            >
                                {bloodGroupings[systemId]?.display_name || systemId}
                            </text>
                            {isSelected && (
                                <rect
                                    x={padding.left - 8}
                                    y={padding.top + (idx * rowHeight)}
                                    width={chartWidth + 8}
                                    height={rowHeight}
                                    fill="none"
                                    stroke="#000"
                                    strokeWidth="2"
                                    pointerEvents="none"
                                />
                            )}
                        </g>
                    );
                })}

                {/* Heatmap cells - gradient rectangles between timestamps */}
                {systemIds.map((systemId, sysIdx) => {
                    const y = padding.top + (sysIdx * rowHeight);
                    const isHovered = hoveredSystem === systemId;
                    const rowOpacity = hoveredSystem ? (isHovered ? 1 : 0.7) : 1;

                    return (
                        <g
                            key={`row-${systemId}`}
                            style={{ opacity: rowOpacity, transition: 'opacity 0.15s ease' }}
                            onMouseEnter={() => setHoveredSystem(systemId)}
                            onMouseLeave={() => setHoveredSystem(null)}
                        >
                            {chartData.slice(0, -1).map((point, idx) => {
                                const nextPoint = chartData[idx + 1];
                                const x1 = padding.left + xScale(point.adjustedTimestamp);
                                const x2 = padding.left + xScale(nextPoint.adjustedTimestamp);
                                const cellWidth = x2 - x1;

                                return (
                                    <rect
                                        key={`cell-${systemId}-${idx}`}
                                        x={x1}
                                        y={y}
                                        width={cellWidth}
                                        height={rowHeight}
                                        fill={`url(#gradient-${systemId}-${idx})`}
                                        stroke="#fff"
                                        strokeWidth="0.5"
                                        style={{ cursor: 'pointer' }}
                                        onMouseMove={(e) => {
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const relX = e.clientX - rect.left;
                                            const ratio = relX / cellWidth;
                                            // Snap to nearest test date instead of interpolating
                                            const useFirst = ratio < 0.5;
                                            const nearestPoint = useFirst ? point : nextPoint;
                                            setHoveredPoint({
                                                systemId,
                                                score: nearestPoint.scores[systemId],
                                                testdate: nearestPoint.testdate,
                                                x: e.clientX,
                                                y: e.clientY
                                            });
                                        }}
                                        onMouseLeave={() => setHoveredPoint(null)}
                                    />
                                );
                            })}
                        </g>
                    );
                })}

                {/* X-axis (timestamps) */}
                <line
                    x1={padding.left}
                    y1={height - padding.bottom}
                    x2={width - padding.right}
                    y2={height - padding.bottom}
                    stroke="#ccc"
                    strokeWidth="1"
                />

                {/* X-axis labels */}
                {chartData.map((point, idx) => {
                    const x = padding.left + xScale(point.adjustedTimestamp);
                    return (
                        <g key={`xaxis-${idx}`}>
                            <line
                                x1={x}
                                y1={height - padding.bottom}
                                x2={x}
                                y2={height - padding.bottom + 5}
                                stroke="#666"
                                strokeWidth="1"
                            />
                            <text
                                x={x}
                                y={height - padding.bottom + 20}
                                textAnchor="middle"
                                style={{ fontSize: '11px', fill: '#666' }}
                            >
                                {formatTimestamp(point.adjustedTimestamp)}
                            </text>
                        </g>
                    );
                })}

                {/* Legend */}
                <g transform={`translate(${width - padding.right + 20}, ${padding.top})`}>
                    <text x={0} y={0} style={{ fontSize: '12px', fontWeight: 'bold', fill: '#333' }}>
                        Health Score
                    </text>

                    {/* Optimal (Green) */}
                    <rect x={0} y={15} width={20} height={15} fill="rgba(34, 197, 94, 1)" />
                    <text x={25} y={27} style={{ fontSize: '11px', fill: '#666' }}>85-95 Optimal</text>

                    {/* Dysfunctional (Yellow) */}
                    <rect x={0} y={35} width={20} height={15} fill="rgba(234, 179, 8, 1)" />
                    <text x={25} y={47} style={{ fontSize: '11px', fill: '#666' }}>50-84 Attention</text>

                    {/* Abnormal (Red) */}
                    <rect x={0} y={55} width={20} height={15} fill="rgba(239, 68, 68, 1)" />
                    <text x={25} y={67} style={{ fontSize: '11px', fill: '#666' }}>0-49 Action</text>

                    {/* Insufficient Data (Gray) */}
                    <rect x={0} y={75} width={20} height={15} fill="rgba(156, 163, 175, 1)" />
                    <text x={25} y={87} style={{ fontSize: '11px', fill: '#666' }}>Insufficient</text>
                </g>
            </svg>

            {/* Tooltip */}
            {hoveredPoint && (
                <div
                    style={{
                        position: 'fixed',
                        left: hoveredPoint.x + 10,
                        top: hoveredPoint.y - 40,
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        pointerEvents: 'none',
                        zIndex: 1000,
                        fontSize: '13px'
                    }}
                >
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        {bloodGroupings[hoveredPoint.systemId]?.display_name || hoveredPoint.systemId}
                    </div>
                    <div style={{ color: '#666' }}>
                        {formatDate(hoveredPoint.testdate)}
                    </div>
                    <div style={{ color: getScoreColor(hoveredPoint.score), fontWeight: 'bold' }}>
                        Score: {hoveredPoint.score != null ? Math.round(hoveredPoint.score) : 'N/A'}
                    </div>
                </div>
            )}
        </div>
    );
}

export default OrganSystemHeatmap;
